import type { calendarEn } from './en.ts'

function russianRaidCount(count: number): string {
  const mod100 = Math.abs(count) % 100
  const mod10 = mod100 % 10
  if (mod100 >= 11 && mod100 <= 14) return 'рейдов'
  if (mod10 === 1) return 'рейд'
  if (mod10 >= 2 && mod10 <= 4) return 'рейда'
  return 'рейдов'
}

function russianCount(count: number, one: string, few: string, many: string): string {
  const mod100 = Math.abs(count) % 100
  const mod10 = mod100 % 10
  if (mod100 >= 11 && mod100 <= 14) return many
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

export const calendarRu: typeof calendarEn = {
  common: {
    optional: 'необязательно',
    minutesShort: (minutes: number) => `${minutes} мин`,
  },
  view: {
    regionLabel: 'Календарь рейдов',
    chooseGuildTitle: 'Выберите гильдию, чтобы открыть её календарь рейдов.',
    chooseGuildDescription: 'Рейды доступны участникам выбранной гильдии Discord.',
    signInTitle: 'Войдите через Discord, чтобы открыть календарь рейдов этой гильдии.',
    signInDescription: 'Участники могут просматривать рейды, а офицеры — создавать, изменять и удалять их.',
    scrollHint: 'Проведите в сторону, чтобы увидеть все дни →',
    loading: 'Загрузка рейдов…',
  },
  header: {
    toolbarLabel: 'Панель календаря',
    week: 'Неделя',
    previousWeek: 'Предыдущая неделя',
    currentWeek: 'Перейти к текущей неделе',
    today: 'Сегодня',
    nextWeek: 'Следующая неделя',
    planRaidLabel: 'Запланировать новый рейд',
    planRaid: '+ Запланировать рейд',
  },
  status: {
    retry: 'Повторить',
    dismiss: 'Закрыть',
    viewOnly: 'Календарь доступен только для просмотра — планировать и изменять рейды могут только офицеры и владелец сервера Discord.',
    cloneSource: 'Выберите рейд в календаре, чтобы скопировать его состав в новый запланированный рейд.',
    loadError: 'Не удалось загрузить рейды на эту неделю.',
    scheduleError: 'Не удалось перенести рейд на выбранное время.',
    openError: 'Не удалось открыть рейд.',
  },
  grid: {
    gridLabel: (weekLabel: string) => `Календарь на неделю ${weekLabel}`,
    time: 'Время',
    cloneSetupFrom: (raidLabel: string) => `Скопировать состав из рейда: ${raidLabel}`,
    openPlanFor: (raidLabel: string) => `Открыть план рейда: ${raidLabel}`,
    actionsFor: (raidName: string) => `Действия с рейдом «${raidName}»`,
    raidActions: 'Действия с рейдом',
    publish: 'Опубликовать',
    unpublish: 'Снять с публикации',
    publishRaid: (raidName: string) => `Опубликовать рейд «${raidName}»`,
    unpublishRaid: (raidName: string) => `Снять рейд «${raidName}» с публикации`,
    editSchedule: 'Изменить расписание',
    editScheduleFor: (raidName: string) => `Изменить расписание рейда «${raidName}»`,
  },
  miniMonth: {
    navigatorLabel: (monthLabel: string) => `Навигация по месяцам: ${monthLabel}`,
    previousMonth: 'Предыдущий месяц',
    nextMonth: 'Следующий месяц',
    chooseCurrent: (monthLabel: string) => `Выбрать месяц и год. Сейчас: ${monthLabel}`,
    choose: 'Выбрать месяц и год',
    previousYear: 'Предыдущий год',
    nextYear: 'Следующий год',
    year: 'Год',
    monthsInYear: (year: number) => `Месяцы ${year} года`,
    selectedWeek: (dateLabel: string) => `${dateLabel}, выбранная неделя`,
  },
  schedule: {
    eyebrow: 'Расписание рейда',
    planTitle: 'Запланировать рейд',
    editTitle: 'Изменить рейд',
    oldRaidReadOnly: 'Этот рейд уже завершён. Редактировать его может только суперадминистратор.',
    closeLabel: 'Закрыть расписание рейда',
    name: 'Название рейда',
    characterCount: (count: number, max: number) => `${count}/${max} символов`,
    copyFrom: 'Скопировать состав из рейда',
    currentBoard: 'Начать с текущего состава',
    copyHelp: 'Копируются комнаты, группы, игроки, сборки и этапы выбранного рейда.',
    date: 'Дата',
    startTime: 'Время начала',
    duration: 'Продолжительность в минутах',
    notes: 'Заметки',
    deleteRaid: 'Удалить рейд',
    cancelRaid: 'Отменить рейд',
    close: 'Закрыть',
    saving: 'Сохранение…',
    save: 'Сохранить расписание',
    invalidName: (max: number) => `Введите название рейда длиной от 1 до ${max} символов.`,
    invalidDateTime: 'Выберите корректные дату и время начала.',
    cancelConfirm: (raidName: string) => `Отменить рейд «${raidName}»?`,
    deleteError: 'Не удалось удалить рейд. Попробуйте ещё раз.',
  },
  recurrence: {
    enabled: 'Сделать рейд повторяющимся',
    cadence: 'Периодичность',
    occurrences: 'Количество повторений',
    weekly: 'Повторяется каждые 7 дней',
    biweekly: 'Повторяется каждые 14 дней',
  },
  selector: {
    toolbarLabel: 'Выбор рейда',
    raid: 'Рейд',
    liveBoardLabel: 'Открыть текущий состав',
    liveBoard: 'Текущий состав',
    scheduledFor: (date: string) => `Запланировано на ${date}`,
    titleScheduled: (raidName: string, date: string) => `${raidName} — запланирован на ${date}`,
    ariaRaid: (raidName: string) => `Рейд «${raidName}»`,
    ariaRaidScheduled: (raidName: string, date: string) => `Рейд «${raidName}» запланирован на ${date}`,
    actionsLabel: 'Действия с рейдом',
    apply: 'Применить к составу',
    delete: 'Удалить',
  },
  planNavigation: {
    navigationLabel: 'Навигация по планам рейдов',
    currentGvg: 'Текущий GvG',
    previous: 'Предыдущий рейд',
    next: 'Следующий рейд',
    previousUnavailable: 'Нет предыдущего запланированного рейда',
    nextUnavailable: 'Нет следующего запланированного рейда',
    previousAria: (raidName: string) => `Открыть предыдущий запланированный рейд: ${raidName}`,
    nextAria: (raidName: string) => `Открыть следующий запланированный рейд: ${raidName}`,
  },
  stage: {
    toolbarLabel: 'Выбор этапа',
    stage: 'Этап',
    all: 'Все этапы',
    loading: 'Загрузка этапа…',
    addLabel: 'Добавить этап',
    add: '+ Добавить этап',
    renameLabel: 'Переименовать выбранный пользовательский этап',
    rename: 'Переименовать',
    name: 'Название пользовательского этапа',
    nameCount: (count: number, max: number) => `${count}/${max}`,
    saveName: 'Сохранить название',
    cancel: 'Отмена',
    renaming: 'Сохранение…',
    nameRequired: 'Введите название пользовательского этапа.',
    nameTooLong: (max: number) => `Название пользовательского этапа должно содержать не более ${max} символов.`,
    renameError: 'Не удалось переименовать пользовательский этап. Попробуйте ещё раз.',
    removeLabel: 'Удалить активный этап',
    remove: 'Удалить',
    opening: 'Начало',
    forest: 'Лес',
    towers: 'Башни',
    goose: 'Гусь',
    tree: 'Дерево',
    custom: 'Другой',
    ariaStage: (stageName: string) => `Этап «${stageName}»`,
  },
  stageDiff: {
    eyebrow: 'Отличия этапов',
    titleAll: 'Изменения относительно «Все этапы»',
    titleCurrent: (stageName: string) => `${stageName}: изменения относительно «Все этапы»`,
    summary: (playerCount: number, stageCount: number) =>
      `${playerCount} ${russianCount(playerCount, 'игрок', 'игрока', 'игроков')} · ${stageCount} ${russianCount(stageCount, 'этап', 'этапа', 'этапов')}`,
    playerStageSummary: (count: number) =>
      `${count} ${russianCount(count, 'изменённый этап', 'изменённых этапа', 'изменённых этапов')}`,
    playerChanges: (playerName: string) => `Изменения этапов игрока ${playerName}`,
    emptyAll: 'Изменений для отдельных этапов пока нет.',
    emptyCurrent: 'Этот этап сейчас совпадает с «Все этапы».',
    stageLabel: (stageName: string) => `Этап: ${stageName}`,
    move: 'Группа',
    moveChange: (source: string, target: string) => `Перемещение из ${source} в ${target}`,
    build: 'Сборка',
    buildChange: 'Активная исходная и целевая сборка',
    sourceBuild: 'Исходная сборка из «Все этапы»',
    targetBuild: 'Целевая сборка этого этапа',
    buildNumber: (number: number) => `Сборка ${number}`,
    emptyBuild: 'Нет активной сборки',
    emptyWeapons: 'Нет оружия',
    buildSnapshot: (number: number, weapons: string) => `Сборка ${number}: ${weapons}`,
    tags: 'Теги',
    addedTag: (tagName: string) => `Добавлен тег ${tagName}`,
    removedTag: (tagName: string) => `Удалён тег ${tagName}`,
    unassigned: 'Без группы',
    resetCurrent: 'Сбросить изменения этого этапа',
    resetAll: 'Сбросить изменения всех этапов',
    confirmCurrentTitle: 'Сбросить этот этап?',
    confirmAllTitle: 'Сбросить все этапы?',
    confirmCurrentBody: 'Все отличия расположения, сборок и тегов выбранного этапа будут удалены. «Все этапы» не изменится.',
    confirmAllBody: 'Все отличия расположения, сборок и тегов неосновных этапов будут удалены. «Все этапы» не изменится.',
    cancel: 'Отмена',
    confirm: 'Сбросить изменения',
    resetting: 'Сброс…',
    resetError: 'Не удалось сбросить изменения этапа. Попробуйте ещё раз.',
  },
  formatters: {
    unscheduled: 'Не запланирован',
    plannedRaidCount: (date: string, count: number) =>
      `${date}, запланировано ${count} ${russianRaidCount(count)}`,
    raidAria: (
      raidName: string,
      time: string,
      duration: number,
      status: string,
      publication: string,
    ) => `${raidName}, ${time}, ${duration} мин, ${status}, ${publication}`,
    published: 'опубликован',
    unpublished: 'не опубликован',
    statusScheduled: 'статус: запланировано',
    statusInProgress: 'статус: идёт сейчас',
    statusCompleted: 'статус: завершено',
    statusCancelled: 'статус: отменено',
    statusOverdue: 'статус: просрочено',
  },
}
